import { useCallback, useEffect, useState } from "react";
import { updateGuestProfile } from "../services/reservationService";
import {
  CHECKIN_LANGUAGE_STORAGE_KEY,
  normalizeCheckinLanguage,
} from "../i18n/checkin";

const APP_LANGUAGE_EVENT = "guestified-language-change";

const getStoredLanguage = () =>
  normalizeCheckinLanguage(localStorage.getItem(CHECKIN_LANGUAGE_STORAGE_KEY));

const buildLanguagePayload = (language) => ({
  language,
  first_name: localStorage.getItem("guest-firstname") || "",
  last_name: localStorage.getItem("guest-lastname") || "",
  email: localStorage.getItem("email") || "",
  phone: localStorage.getItem("telephone") || "",
  room_number: localStorage.getItem("room-number") || "",
});

export const saveAppLanguageToBackend = async (language) => {
  const reservationCode = localStorage.getItem("reservation-code");

  if (!reservationCode) {
    return null;
  }

  return updateGuestProfile(reservationCode, buildLanguagePayload(language));
};

export const setStoredAppLanguage = (language) => {
  const normalizedLanguage = normalizeCheckinLanguage(language);
  localStorage.setItem(CHECKIN_LANGUAGE_STORAGE_KEY, normalizedLanguage);
  window.dispatchEvent(new CustomEvent(APP_LANGUAGE_EVENT, {
    detail: { language: normalizedLanguage },
  }));
  return normalizedLanguage;
};

export const useAppLanguage = () => {
  const [language, setLanguageState] = useState(getStoredLanguage);

  useEffect(() => {
    const handleLanguageChange = (event) => {
      setLanguageState(normalizeCheckinLanguage(event.detail?.language));
    };

    window.addEventListener(APP_LANGUAGE_EVENT, handleLanguageChange);
    window.addEventListener("storage", handleLanguageChange);

    return () => {
      window.removeEventListener(APP_LANGUAGE_EVENT, handleLanguageChange);
      window.removeEventListener("storage", handleLanguageChange);
    };
  }, []);

  const setLanguage = useCallback(async (nextLanguage, options = {}) => {
    const normalizedLanguage = setStoredAppLanguage(nextLanguage);
    setLanguageState(normalizedLanguage);

    if (options.persist !== false) {
      try {
        await saveAppLanguageToBackend(normalizedLanguage);
      } catch (error) {
        console.error("Failed to persist preferred language", error);
      }
    }

    return normalizedLanguage;
  }, []);

  return [language, setLanguage];
};
